import { useState } from "react";
import { useWorld } from "@/context/WorldContext";

// In a fanon profile, entries without a canon_id were created there (originals); the rest came from canon
export const isOriginal = (page, profile) => !!profile && !page.canon_id;

export const OriginBadge = ({ page, className = "" }) => {
  const { profile } = useWorld();
  if (!isOriginal(page, profile)) return null;
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border border-violet-400/40 bg-violet-500/10 px-1.5 py-px align-middle text-[9px] font-code uppercase tracking-widest text-violet-300 ${className}`}
      data-testid={`origin-badge-${page.id}`}>Original</span>
  );
};

const OPTIONS = [["all", "All"], ["original", "Originals"], ["canon", "From canon"]];

const linkedIds = (p) => (p.sections || []).flatMap((s) => (s.facts || []).flatMap((f) => (f.items || []).map((i) => i.page_id))).filter(Boolean);

// "Originals" keeps original entries plus whatever they link to, so an OC's canon partner or parent still shows
const withConnections = (pages, relationships, profile) => {
  const orig = new Set(pages.filter((p) => isOriginal(p, profile)).map((p) => p.id));
  const keep = new Set(orig);
  pages.forEach((p) => {
    const links = linkedIds(p);
    if (orig.has(p.id)) links.forEach((id) => keep.add(id));
    else if (links.some((id) => orig.has(id))) { keep.add(p.id); links.forEach((id) => keep.add(id)); }
  });
  (relationships || []).forEach((r) => {
    if (orig.has(r.source_id) || orig.has(r.target_id)) { keep.add(r.source_id); keep.add(r.target_id); }
  });
  return pages.filter((p) => keep.has(p.id));
};

// Filtered pages plus the All / Originals / From canon toggle (only inside a profile)
export const useOrigin = () => {
  const { pages, relationships, profile } = useWorld();
  const [mode, setMode] = useState("all");
  const shown = !profile || mode === "all" ? pages
    : mode === "original" ? withConnections(pages, relationships, profile) : pages.filter((p) => !isOriginal(p, profile));
  const toggle = profile ? (
    <div className="flex w-fit rounded-full border border-violet-400/40 p-0.5 text-xs" data-testid="origin-filter">
      {OPTIONS.map(([k, l]) => (
        <button key={k} onClick={() => setMode(k)} aria-pressed={mode === k} data-testid={`origin-filter-${k}`}
          className={`rounded-full px-3 py-1 transition-colors ${mode === k ? "bg-violet-500/20 text-violet-100" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
      ))}
    </div>
  ) : null;
  return { pages: shown, toggle, mode: profile ? mode : "all", isOC: (p) => isOriginal(p, profile) };
};

// Splits entries into originals and canon copies for profile views
export const splitOrigin = (items, profile) => [
  { key: "original", name: "Original to this profile", items: items.filter((p) => isOriginal(p, profile)) },
  { key: "canon", name: "From canon", items: items.filter((p) => !isOriginal(p, profile)) },
];
